import { type ReactElement, useContext } from "react";
import { ResponsiveContainer } from "recharts";
import { ChartPreviewContext } from "./ChartPreviewContext";

interface ChartCardProps {
  title: string;
  /** Plain-text takeaway — also serves as the chart's accessible description and SEO text. */
  caption: string;
  /** Recharts chart element (a single child, as ResponsiveContainer requires). */
  children: ReactElement;
  /** Chart height in pixels. */
  height?: number;
}

/**
 * Bordered card wrapping a chart in a <figure>, with a small heading and a
 * visible text caption. Recharts renders SVG (not crawlable / not read by
 * screen readers), so the caption carries the meaning for both SEO and a11y.
 */
const ChartCard = ({ title, caption, children, height = 240 }: ChartCardProps) => {
  const preview = useContext(ChartPreviewContext);

  // Thumbnail mode: just the chart body, filling its container, no chrome —
  // same treatment as MetricChartFrame. The `chart-preview` class hides bar
  // value labels and `chart-preview-card` additionally hides the long
  // categorical X-axis labels (see index.css) so tiny previews stay clean.
  if (preview) {
    return (
      <div className="chart-preview chart-preview-card h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <figure
      role="img"
      aria-label={`${title}. ${caption}`}
      className="my-8 rounded-2xl border border-border bg-card p-5 md:p-6"
    >
      {/* Not a heading: charts land at arbitrary depths in articles (often
          before the first h2), so a heading tag here breaks the outline. */}
      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-4">{title}</p>
      <div style={{ height }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
      <figcaption className="mt-4 text-sm text-muted-foreground leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  );
};

export default ChartCard;
